/**
 * ApplicationsTab.js - Worker Applications management for Owner Dashboard
 * Handles viewing, approving, and rejecting worker applications
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  UserPlus, Check, X, Eye, FileText, Phone, Mail, MapPin,
  Calendar, Clock, Briefcase, ChevronDown, ChevronUp, AlertTriangle,
  Edit, Trash2, Archive, MessageSquare
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../services/api';
import MobileActionBar from '../common/MobileActionBar';

const ApplicationsTab = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedApp, setExpandedApp] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  
  // Rejection modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingApp, setRejectingApp] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionReasons, setRejectionReasons] = useState([]);

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/applications');
      setApplications(res.data || []);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch rejection reasons
  const fetchRejectionReasons = useCallback(async () => {
    try {
      const res = await api.get('/applications/rejection-reasons');
      setRejectionReasons(res.data?.reasons || []);
    } catch (error) {
      console.error('Failed to fetch rejection reasons:', error);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
    fetchRejectionReasons();
  }, [fetchApplications, fetchRejectionReasons]);

  // Approve application
  const approveApplication = async (appId) => {
    setProcessingId(appId);
    try {
      await api.post(`/applications/${appId}/approve`);
      toast.success('Application approved! Worker profile created.');
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    } finally {
      setProcessingId(null);
    }
  };

  // Reject application
  const rejectApplication = async () => {
    if (!rejectingApp || !rejectionReason) {
      toast.error('Please select a rejection reason');
      return;
    }
    
    setProcessingId(rejectingApp.id);
    try {
      await api.post(`/applications/${rejectingApp.id}/reject`, null, {
        params: { reason: rejectionReason }
      });
      toast.success('Application rejected');
      setShowRejectModal(false);
      setRejectingApp(null);
      setRejectionReason('');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to reject application');
    } finally {
      setProcessingId(null);
    }
  };

  // Delete application
  const deleteApplication = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    
    setProcessingId(appId);
    try {
      await api.delete(`/applications/${appId}`);
      toast.success('Application deleted');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to delete application');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'approved': return 'bg-[var(--success)]/15 text-[var(--success)]';
      case 'rejected': return 'bg-[var(--error)]/15 text-[var(--error)]';
      default: return 'bg-[var(--background-subtle)] text-[var(--text-primary)]';
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="applications-tab">
      <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond'] mb-8">Worker Applications</h1>
      
      {loading ? (
        <div className="card p-8 text-center">
          <div className="w-8 h-8 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-2 text-[var(--text-muted)]">Loading applications...</p>
        </div>
      ) : applications.length === 0 ? (
        <div className="card p-12 text-center">
          <UserPlus className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <p className="text-[var(--text-secondary)]">No applications yet</p>
          <p className="text-sm text-[var(--text-muted)] mt-2">
            New worker applications will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {applications.map(app => (
            <div key={app.id} className="card overflow-hidden" data-testid={`app-card-${app.id}`}>
              {/* Application Header */}
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 rounded-full bg-[var(--primary-light)] flex items-center justify-center">
                        <span className="text-[var(--primary)] font-bold text-lg">
                          {app.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold">{app.full_name}</h3>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                          {app.status}
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-[var(--text-muted)]">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${app.email}`} className="hover:text-[var(--primary)]">{app.email}</a>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${app.phone}`} className="hover:text-[var(--primary)]">{app.phone}</a>
                      </div>
                      {app.address && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{app.address}, {app.city}, {app.state} {app.zip_code}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>Applied: {new Date(app.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                    >
                      {expandedApp === app.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      {expandedApp === app.id ? 'Less' : 'More'}
                    </Button>
                    
                    {app.status === 'pending' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => approveApplication(app.id)}
                          disabled={processingId === app.id}
                          className="text-[var(--success)] border-green-600 hover:bg-[var(--success)]/10"
                          data-testid={`approve-${app.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setRejectingApp(app); setShowRejectModal(true); }}
                          disabled={processingId === app.id}
                          className="text-[var(--error)] border-red-600 hover:bg-[var(--error)]/10"
                          data-testid={`reject-${app.id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteApplication(app.id)}
                      disabled={processingId === app.id}
                      className="text-[var(--text-muted)] hover:text-[var(--error)]"
                      data-testid={`delete-${app.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Expanded Details */}
              {expandedApp === app.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="border-t bg-[var(--background-subtle)]"
                >
                  <div className="p-6 space-y-6">
                    {/* Services Applied For */}
                    {app.service_types?.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <Briefcase className="w-4 h-4" />
                          Services Applied For
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {app.service_types.map(service => (
                            <span key={service} className="px-3 py-1 bg-[var(--primary-light)] text-[var(--primary)] rounded-full text-sm">
                              {service}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Availability */}
                    {app.availability && (
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Availability
                        </h4>
                        <div className="grid grid-cols-7 gap-2">
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => {
                            const isAvailable = app.availability[day.toLowerCase()];
                            return (
                              <div 
                                key={day}
                                className={`text-center p-2 rounded ${
                                  isAvailable ? 'bg-[var(--success)]/15 text-[var(--success)]' : 'bg-[var(--background-subtle)] text-gray-400'
                                }`}
                              >
                                {day}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Documents */}
                    {app.documents?.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Uploaded Documents
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {app.documents.map((doc, idx) => (
                            <a
                              key={`doc-${doc.type || doc.document_type}-${idx}`}
                              href={doc.file_url || doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 p-3 bg-[var(--background-paper)] rounded-lg border hover:border-[var(--primary)] transition-colors"
                            >
                              <FileText className="w-8 h-8 text-[var(--primary)]" />
                              <div>
                                <p className="font-medium">{doc.type || doc.document_type}</p>
                                {doc.expiration_date && (
                                  <p className="text-xs text-[var(--text-muted)]">
                                    Expires: {new Date(doc.expiration_date).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Experience */}
                    {app.experience && (
                      <div>
                        <h4 className="font-semibold mb-2">Experience & Background</h4>
                        <p className="text-[var(--text-secondary)] bg-[var(--background-paper)] p-4 rounded-lg border">
                          {app.experience}
                        </p>
                      </div>
                    )}
                    
                    {/* Emergency Contact */}
                    {app.emergency_contact && (
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                          Emergency Contact
                        </h4>
                        <div className="bg-[var(--background-paper)] p-4 rounded-lg border">
                          <p className="font-medium">{app.emergency_contact.name}</p>
                          <p className="text-sm text-[var(--text-muted)]">{app.emergency_contact.relationship}</p>
                          <p className="text-sm">{app.emergency_contact.phone}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectModal && rejectingApp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-xl shadow-2xl max-w-md w-full"
          >
            <div className="p-4 border-b">
              <h2 className="text-xl font-bold">Reject Application</h2>
              <p className="text-sm text-[var(--text-muted)]">
                Rejecting application from {rejectingApp.full_name}
              </p>
            </div>
            <div className="p-4">
              <label className="block text-sm font-medium mb-2">Rejection Reason</label>
              <select
                className="w-full p-3 border rounded-lg mb-4"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              >
                <option value="">Select a reason...</option>
                {rejectionReasons.map(reason => (
                  <option key={reason.id} value={reason.id}>{reason.label}</option>
                ))}
              </select>
              
              {rejectionReason === 'other' && (
                <textarea
                  className="w-full p-3 border rounded-lg resize-none"
                  rows={3}
                  placeholder="Enter custom reason..."
                  onChange={(e) => setRejectionReason(e.target.value)}
                />
              )}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowRejectModal(false); setRejectingApp(null); }}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={rejectApplication}
                disabled={!rejectionReason || processingId === rejectingApp.id}
              >
                {processingId === rejectingApp.id ? 'Processing...' : 'Reject Application'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Mobile Action Bar - Review next pending application */}
      <MobileActionBar
        actions={[
          ...(applications.filter(a => a.status === 'pending').length > 0 ? [{
            id: 'review-next',
            label: `Review ${applications.filter(a => a.status === 'pending').length} Pending`,
            icon: 'Eye',
            variant: 'warning',
            onClick: () => {
              const pending = applications.find(a => a.status === 'pending');
              if (pending) setExpandedApp(pending.id);
            }
          }] : [])
        ]}
      />
    </motion.div>
  );
};

export default ApplicationsTab;
